<template>
    <div class="mb-10 rounded-xl border border-gray-200 bg-white p-6">
        <div class="mb-6">
            <h3 class="font-medium text-gray-900">
                {{ $t("vue.email_volume_over_time") }}
            </h3>
            <p class="mt-1 text-sm text-gray-600">{{ props.header }}</p>
        </div>
        <div class="mb-5 h-80">
            <div ref="chartDiv" style="width: 100%; height: 300px"></div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { defineProps, onMounted, ref } from "vue";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import { useTranslations } from "@/composables/useTranslations";

const { t } = useTranslations();

const props = defineProps<{
    chartData: Array<{
        day: string;
        pass: number;
        fail: number;
    }>;
    header: string;
}>();

const chartDiv = ref(null);
let root;

onMounted(() => {
    // Create root element
    root = am5.Root.new(chartDiv.value);

    // Set themes
    root.setThemes([am5.Theme.new(root)]);
    root._logo.dispose();

    // Add chart
    let chart = root.container.children.push(
        am5xy.XYChart.new(root, {
            layout: root.verticalLayout,
            paddingTop: 10,
            paddingBottom: 20,
        }),
    );

    // Add cursor
    chart.set("cursor", am5xy.XYCursor.new(root, {}));

    // Create X-Axis
    let xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
            categoryField: "day",
            renderer: am5xy.AxisRendererX.new(root, {
                minGridDistance: 60,
            }),
            tooltip: am5.Tooltip.new(root, {}),
        }),
    );
    xAxis.get("renderer").labels.template.setAll({
        fill: am5.color("#6B7280"),
        fontSize: 12,
    });

    // Show fewer labels when there are many dates to prevent overlap
    xAxis
        .get("renderer")
        .labels.template.adapters.add("text", function (text, target) {
            const dataItem = target.dataItem;
            if (dataItem) {
                const index = dataItem.get("index");
                const totalItems = props.chartData.length;

                // Skip labels based on data length
                let skipFactor = 1;
                if (totalItems > 60)
                    skipFactor = 7; // Show every 7th label
                else if (totalItems > 30)
                    skipFactor = 3; // Show every 3rd label
                else if (totalItems > 14) skipFactor = 2; // Show every 2nd label

                if (index % skipFactor !== 0) {
                    return "";
                }
            }
            return text;
        });

    // Create Y-Axis
    let yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
            renderer: am5xy.AxisRendererY.new(root, {}),
        }),
    );
    yAxis.get("renderer").labels.template.setAll({
        fill: am5.color("#6B7280"),
        fontSize: 12,
    });

    // Chart Data
    const data = props.chartData.map((item) => ({
        ...item,
        day: new Date(item.day).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
        }), // Format like "Jun 19"
    }));

    // Generate series function
    // eslint-disable-next-line no-inner-declarations
    function createSeries(name, field, color) {
        let series = chart.series.push(
            am5xy.ColumnSeries.new(root, {
                name: name,
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: field,
                categoryXField: "day",
                stacked: true, // This ensures that the series are stacked
                tooltip: am5.Tooltip.new(root, {
                    labelText: "{name}: {valueY}",
                }),
                fill: am5.color(color),
                stroke: am5.color(color),
            }),
        );

        series.columns.template.setAll({
            tooltipText: "{name}: {valueY}",
            fillOpacity: 1,
            strokeOpacity: 0,
        });

        series.data.setAll(data);
    }

    // Series
    createSeries(t("vue.valid_dmarc"), "pass", "#10B981"); // Tailwind green-500
    createSeries(t("vue.invalid_dmarc"), "fail", "#F87171"); // Tailwind red-400

    xAxis.data.setAll(data);
});
</script>
